/*
 * Toasts: a short note that an action finished ("Job 161 queued") or failed. Not in Figma.
 * Bottom right (full width less the gutter on a phone), newest at the bottom, at most 4.
 * Each: --surface-raised, radius 8, --shadow-popout, 1px --border-default, padding 12 12 12 16,
 * a 16px tone icon, 400 14/20 text, a close button. Success and info go after 5 s, a failure
 * after 10 s; hovering or focusing one keeps it. Announced politely; failures as alerts.
 *
 * Wrap the app in <ToastProvider closeLabel=…>, then `const toast = useToast()` and
 * `toast({ tone: 'success', message: '…' })`.
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cx } from './cx'
import { IconButton } from './IconButton'
import { checkCircle16, info16, warning16, x16Close } from './icons'
import { TintedIcon } from './TintedIcon'

export type ToastTone = 'success' | 'danger' | 'info'

export interface ToastInput {
    tone: ToastTone
    message: ReactNode
}

interface ToastItem extends ToastInput {
    id: number
}

const ToastContext = createContext<(toast: ToastInput) => void>(() => {})

/** Show a toast (inside a ToastProvider; outside one it does nothing). */
export function useToast(): (toast: ToastInput) => void {
    return useContext(ToastContext)
}

const ICON: Record<ToastTone, { src: string; colour: string }> = {
    success: { src: checkCircle16, colour: 'text-[var(--status-success)]' },
    danger: { src: warning16, colour: 'text-[var(--status-danger)]' },
    info: { src: info16, colour: 'text-[var(--status-info)]' },
}

function Toast({ item, closeLabel, onClose }: { item: ToastItem; closeLabel: string; onClose: () => void }) {
    const [held, setHeld] = useState(false)
    const close = useRef(onClose)
    useLayoutEffect(() => {
        close.current = onClose
    })
    useEffect(() => {
        if (held) return
        const timer = window.setTimeout(() => close.current(), item.tone === 'danger' ? 10_000 : 5_000)
        return () => window.clearTimeout(timer)
    }, [held, item.tone])
    return (
        <li
            role={item.tone === 'danger' ? 'alert' : 'status'}
            data-testid={`toast-${item.tone}`}
            onMouseEnter={() => setHeld(true)}
            onMouseLeave={() => setHeld(false)}
            onFocus={() => setHeld(true)}
            onBlur={() => setHeld(false)}
            className="pointer-events-auto flex w-[380px] max-w-full items-start gap-[10px] rounded-[8px] border border-solid border-[var(--border-default)] bg-[var(--surface-raised)] py-[12px] pr-[12px] pl-[16px] shadow-[var(--shadow-popout)]"
        >
            <TintedIcon src={ICON[item.tone].src} className={cx('mt-[2px]', ICON[item.tone].colour)} />
            <div className="min-w-0 flex-1 pt-[0px] text-[14px] leading-[20px] font-normal text-[var(--text-default)] [overflow-wrap:anywhere]">{item.message}</div>
            <IconButton icon={x16Close} label={closeLabel} onClick={onClose} className="-my-[6px]" />
        </li>
    )
}

export function ToastProvider({ children, closeLabel }: { children: ReactNode; closeLabel: string }) {
    const [items, setItems] = useState<ToastItem[]>([])
    const next = useRef(1)
    const show = useCallback((toast: ToastInput) => {
        const id = next.current++
        setItems((current) => [...current, { ...toast, id }].slice(-4))
    }, [])
    const value = useMemo(() => show, [show])
    return (
        <ToastContext.Provider value={value}>
            {children}
            <ol
                aria-live="polite"
                className="pointer-events-none fixed right-[24px] bottom-[24px] z-[60] m-0 flex list-none flex-col items-end gap-[8px] p-0 max-sm:right-[16px] max-sm:bottom-[16px] max-sm:left-[16px]"
            >
                {items.map((item) => (
                    <Toast key={item.id} item={item} closeLabel={closeLabel} onClose={() => setItems((current) => current.filter((other) => other.id !== item.id))} />
                ))}
            </ol>
        </ToastContext.Provider>
    )
}
